import {PrefixedProjectUid} from "@buildwithflux/identifiers-schema";
import {Schema} from "effect";

/**
 * HTTP wire shapes for the permissions-journal endpoints (append-only staged
 * sharing changes, committed transactionally into the roles/invites view).
 *
 * Identifiers crossing this wire are branded Effect Schema types: document
 * identifiers travel in the canonical prefixed `prj_…` form
 * ({@link PrefixedProjectUid}) and draft identifiers are branded UUIDs.
 * Malformed or bare identifiers fail decoding at the HTTP boundary; a plain
 * `string` is a compile error at call sites.
 *
 * Changes cross the wire opaquely (`Schema.Unknown`): their canonical
 * structural validation is the zod `PermissionsJournalChangeData` union in
 * `../permissionsJournal`, which routes role payloads through the same
 * `DocumentPermissionsData` normalization pipeline as every other role write
 * (all principal uids in those payloads are prefixed — `usr_…`, `org_…`, or
 * the anonymous sentinel).
 */

/** A journal draft identifier: a branded UUID grouping one dialog session's records. */
export const PermissionsJournalDraftUid = Schema.UUID.pipe(Schema.brand("PermissionsJournalDraftUid"));
export type PermissionsJournalDraftUid = typeof PermissionsJournalDraftUid.Type;

const DocumentUidOnWire = PrefixedProjectUid.schema;

export class StagePermissionsJournalRequest extends Schema.Class<StagePermissionsJournalRequest>(
    "StagePermissionsJournalRequest",
)({
    documentUid: DocumentUidOnWire,
    /** When present, the staged batch supersedes the draft's prior open records. */
    draftUid: Schema.optional(PermissionsJournalDraftUid),
    changes: Schema.Array(Schema.Unknown),
}) {}

export class StagePermissionsJournalResponse extends Schema.Class<StagePermissionsJournalResponse>(
    "StagePermissionsJournalResponse",
)({
    draftUid: PermissionsJournalDraftUid,
}) {}

export class StageAndCommitPermissionsJournalRequest extends Schema.Class<StageAndCommitPermissionsJournalRequest>(
    "StageAndCommitPermissionsJournalRequest",
)({
    documentUid: DocumentUidOnWire,
    changes: Schema.Array(Schema.Unknown),
}) {}

export class StageAndCommitPermissionsJournalResponse extends Schema.Class<StageAndCommitPermissionsJournalResponse>(
    "StageAndCommitPermissionsJournalResponse",
)({
    draftUid: PermissionsJournalDraftUid,
    committed: Schema.Boolean,
}) {}

export class CommitPermissionsJournalRequest extends Schema.Class<CommitPermissionsJournalRequest>(
    "CommitPermissionsJournalRequest",
)({
    documentUid: DocumentUidOnWire,
    draftUid: PermissionsJournalDraftUid,
}) {}

export class CommitPermissionsJournalResponse extends Schema.Class<CommitPermissionsJournalResponse>(
    "CommitPermissionsJournalResponse",
)({
    committed: Schema.Boolean,
}) {}

export class DiscardPermissionsJournalRequest extends Schema.Class<DiscardPermissionsJournalRequest>(
    "DiscardPermissionsJournalRequest",
)({
    documentUid: DocumentUidOnWire,
    draftUid: PermissionsJournalDraftUid,
}) {}

export class DiscardPermissionsJournalResponse extends Schema.Class<DiscardPermissionsJournalResponse>(
    "DiscardPermissionsJournalResponse",
)({
    discarded: Schema.Boolean,
}) {}
